import {CheckMarkIcon, StopIcon, CrossIcon, TypographyProps, Typography, Link} from '@genestack/ui';
import classNames from 'classnames';
import React, {ReactElement} from 'react';

import {useCurrentUserQuery} from '../../../hooks/user/use-current-user-query';
import {usePreselectedTab} from '../../../components/layout/page-layout/use-preselected-tab';
import {getFormattedDateTimeShort} from '../../../utils/format-date';
import {formatInteger} from '../../../utils/format-integer';

import queryMPStyles from './query-management-panel.module.css';
import commonMPStyles from '../../../components/management-panel/management-panel.module.css';
import {QueriesTab} from '../../../components/layout/page-layout/layout-typings';
import {QuerySearchDuration} from '../common/query-search-duration';

const statusText: Record<FinishedQuery['status'], string | null> = {
    COMPLETED: 'Search complete',
    STOPPED: 'Stopped',
    FAILED: 'Failed'
};

const statusIcon: Record<FinishedQuery['status'], ReactElement> = {
    COMPLETED: <CheckMarkIcon />,
    STOPPED: <StopIcon />,
    FAILED: <CrossIcon />
};

const statusIntent: Record<FinishedQuery['status'], TypographyProps['intent']> = {
    COMPLETED: 'success',
    STOPPED: 'no-intent',
    FAILED: 'alarm'
};

interface Props {
    query: FinishedQuery;
    isHighlighted?: boolean;
}

export function RecentQuery(props: Props) {
    const currentUser = useCurrentUserQuery();
    const isCreatedByCurrentUser = props.query.owner.email === currentUser.data?.email;
    const {getPreselectedTabURLParam} = usePreselectedTab();

    return (
        <div
            className={classNames(
                commonMPStyles.tableRow,
                queryMPStyles.queryRow,
                commonMPStyles.rowWithHover,
                props.isHighlighted && commonMPStyles.highlightedRow
            )}
        >
            <div className={queryMPStyles.queryName}>
                <Link
                    href={`/queries/${props.query.id}?${
                        isCreatedByCurrentUser ? getPreselectedTabURLParam(QueriesTab.RECENT) : ''
                    }`}
                    target="_blank"
                >
                    {props.query.name}
                </Link>
            </div>

            <Typography>{props.query.owner.name}</Typography>

            <Typography>
                <QuerySearchDuration query={props.query} />
            </Typography>

            <Typography className={queryMPStyles.resultsCount}>
                {formatInteger(props.query.resultsCount)}
            </Typography>

            <Typography intent={statusIntent[props.query.status]}>
                {statusIcon[props.query.status]}
                {statusText[props.query.status]}
            </Typography>

            <Typography intent="quiet">
                {getFormattedDateTimeShort(props.query.finishedAt)}
            </Typography>
        </div>
    );
}
